import { FormEvent, useEffect, useState } from "react";
import { api, json } from "../../lib/api";
import { useAsync } from "../../lib/useAsync";
import { Field, Input, Select, Textarea } from "../../components/Form";
import {
  Empty,
  ErrorState,
  Failure,
  Loading,
  Success,
} from "../../components/State";
import { StatusBadge } from "../../components/Badge";

interface ProgramSummary {
  id: string;
  code: string;
  name: string;
  status: string;
  version: number;
  version_status: string;
}
interface ProgramConfig {
  id: string;
  name: string;
  description: string;
  status: string;
  version_status: string;
  open_from?: string;
  open_until?: string;
  supplement_window_hours: number;
  reapply_cooldown_hours: number;
  schema_json: unknown;
  ui_schema_json: unknown;
  materials: unknown[];
  stages: unknown[];
  checklist_items: unknown[];
}
export function AdminProgramsPage() {
  const list = useAsync(
    () => api<{ items: ProgramSummary[] }>("/admin/admission-config/programs"),
    [],
  );
  const [selected, setSelected] = useState("");
  useEffect(() => {
    if (!selected && list.data?.items[0]) setSelected(list.data.items[0].id);
  }, [list.data, selected]);
  const detail = useAsync(
    () =>
      selected
        ? api<ProgramConfig>(`/admin/admission-config/programs/${selected}`)
        : Promise.resolve(undefined),
    [selected],
  );
  const [form, setForm] = useState<Record<string, string | number>>();
  const [schema, setSchema] = useState("");
  const [uiSchema, setUISchema] = useState("");
  const [materials, setMaterials] = useState("");
  const [stages, setStages] = useState("");
  const [checklist, setChecklist] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (detail.data) {
      setForm({
        name: detail.data.name,
        description: detail.data.description || "",
        open_from: toLocal(detail.data.open_from),
        open_until: toLocal(detail.data.open_until),
        supplement_window_hours: detail.data.supplement_window_hours,
        reapply_cooldown_hours: detail.data.reapply_cooldown_hours,
      });
      setSchema(JSON.stringify(detail.data.schema_json, null, 2));
      setUISchema(JSON.stringify(detail.data.ui_schema_json, null, 2));
      setMaterials(JSON.stringify(detail.data.materials, null, 2));
      setStages(JSON.stringify(detail.data.stages, null, 2));
      setChecklist(JSON.stringify(detail.data.checklist_items, null, 2));
    }
  }, [detail.data]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!form || !selected) return;
    setBusy(true);
    setError("");
    try {
      await api(`/admin/admission-config/programs/${selected}/draft`, {
        method: "PUT",
        body: json({
          ...form,
          open_from: form.open_from
            ? new Date(String(form.open_from)).toISOString()
            : "",
          open_until: form.open_until
            ? new Date(String(form.open_until)).toISOString()
            : "",
          schema_json: JSON.parse(schema),
          ui_schema_json: JSON.parse(uiSchema),
          materials: JSON.parse(materials),
          stages: JSON.parse(stages),
          checklist_items: JSON.parse(checklist),
        }),
      });
      setMessage("项目配置草稿已保存");
      await detail.retry();
      await list.retry();
    } catch (value) {
      setError(value instanceof Error ? value.message : "配置格式或内容无效");
    } finally {
      setBusy(false);
    }
  };
  const publish = async () => {
    setBusy(true);
    setError("");
    try {
      await api(`/admin/admission-config/programs/${selected}/publish`, {
        method: "POST",
        body: json({}),
      });
      setMessage("项目配置新版本已发布");
      await detail.retry();
      await list.retry();
    } catch (value) {
      setError(value instanceof Error ? value.message : "发布失败");
    } finally {
      setBusy(false);
    }
  };
  const status = async (value: string) => {
    setBusy(true);
    setError("");
    try {
      await api(`/admin/admission-config/programs/${selected}/status`, {
        method: "POST",
        body: json({ status: value }),
      });
      setMessage("项目状态已更新");
      await detail.retry();
      await list.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "状态更新失败");
    } finally {
      setBusy(false);
    }
  };
  if (list.loading) return <Loading />;
  if (list.error) return <ErrorState error={list.error} retry={list.retry} />;
  if (!list.data?.items.length) return <Empty title="暂无入驻项目" />;
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>ADMISSION CONFIG</span>
          <h1>项目与表单</h1>
          <p>入驻项目、动态表单、材料、审核流和确认清单版本。</p>
        </div>
      </header>
      {message && <Success>{message}</Success>}
      {error && <Failure>{error}</Failure>}
      <div className="admin-filterbar">
        <Select value={selected} onChange={(e) => setSelected(e.target.value)}>
          {list.data.items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name} · v{item.version}
            </option>
          ))}
        </Select>
        {detail.data && (
          <>
            <StatusBadge status={detail.data.version_status as never} />
            <button
              className="secondary-button compact"
              disabled={busy}
              onClick={() =>
                status(detail.data?.status === "active" ? "closed" : "active")
              }
            >
              {detail.data.status === "active" ? "关闭申请" : "开放申请"}
            </button>
            {detail.data.version_status === "draft" && (
              <button
                className="primary-button compact"
                disabled={busy}
                onClick={publish}
              >
                发布版本
              </button>
            )}
          </>
        )}
      </div>
      {detail.loading || !form ? (
        <Loading />
      ) : detail.error ? (
        <ErrorState error={detail.error} retry={detail.retry} />
      ) : (
        <form onSubmit={save}>
          <section className="admin-panel detail-section">
            <header>
              <div>
                <span>PROGRAM</span>
                <h2>项目规则</h2>
              </div>
            </header>
            <div className="form-grid">
              <Field label="版本名称" required>
                <Input
                  required
                  value={String(form.name)}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
              <Field label="补充期限（小时）" required>
                <Input
                  required
                  type="number"
                  min={1}
                  value={Number(form.supplement_window_hours)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      supplement_window_hours: Number(e.target.value),
                    })
                  }
                />
              </Field>
              <Field label="重新申请冷静期（小时）">
                <Input
                  type="number"
                  min={0}
                  value={Number(form.reapply_cooldown_hours)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      reapply_cooldown_hours: Number(e.target.value),
                    })
                  }
                />
              </Field>
              <Field label="开放时间">
                <Input
                  type="datetime-local"
                  value={String(form.open_from || "")}
                  onChange={(e) =>
                    setForm({ ...form, open_from: e.target.value })
                  }
                />
              </Field>
              <Field label="关闭时间">
                <Input
                  type="datetime-local"
                  value={String(form.open_until || "")}
                  onChange={(e) =>
                    setForm({ ...form, open_until: e.target.value })
                  }
                />
              </Field>
              <Field label="版本说明">
                <Textarea
                  rows={3}
                  value={String(form.description)}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </Field>
            </div>
          </section>
          <section className="admin-panel detail-section">
            <header>
              <div>
                <span>FORM</span>
                <h2>动态表单与材料</h2>
              </div>
            </header>
            <div className="config-editor-grid">
              <Field label="JSON Schema">
                <Textarea
                  className="code-editor"
                  required
                  rows={16}
                  value={schema}
                  onChange={(e) => setSchema(e.target.value)}
                />
              </Field>
              <Field label="UI Schema">
                <Textarea
                  className="code-editor"
                  required
                  rows={16}
                  value={uiSchema}
                  onChange={(e) => setUISchema(e.target.value)}
                />
              </Field>
              <Field label="材料要求">
                <Textarea
                  className="code-editor"
                  required
                  rows={18}
                  value={materials}
                  onChange={(e) => setMaterials(e.target.value)}
                />
              </Field>
            </div>
          </section>
          <section className="admin-panel detail-section">
            <header>
              <div>
                <span>WORKFLOW</span>
                <h2>审核流与线下确认</h2>
              </div>
            </header>
            <div className="config-editor-grid">
              <Field label="审核阶段">
                <Textarea
                  className="code-editor"
                  required
                  rows={18}
                  value={stages}
                  onChange={(e) => setStages(e.target.value)}
                />
              </Field>
              <Field label="确认清单">
                <Textarea
                  className="code-editor"
                  required
                  rows={18}
                  value={checklist}
                  onChange={(e) => setChecklist(e.target.value)}
                />
              </Field>
            </div>
          </section>
          <button className="primary-button" disabled={busy}>
            {busy ? "保存中..." : "保存配置草稿"}
          </button>
        </form>
      )}
    </div>
  );
}
function toLocal(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
